import React, { useEffect } from 'react';
import '../../App.css';
import CSS from 'csstype';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../app/hooks';

// icons

// types relating to events.
import { ServiceStatusParams } from '../../utils/events';

// functions

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

import { ComputeServiceType, Site } from '../../utils/types';

// classes
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import ComputeSession from '../compute-tiles/compute-session';
import SiteIcon from '../../tools/site-icon';
import { TabBar } from '../../tools/tab-component/tab-component';

// import state definition and functions (redux).
import { updateComputeSearchServiceStatus } from './search-compute-actions';

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function SearchComputeTable() {
  //
  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  // state variables.
  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.searchCompute.sLoadingComponent,
  );
  const sUpdateServiceStatus: ServiceStatusParams[] = useAppSelector(
    (state) => state.searchCompute.sUpdateServiceStatus,
  );
  // the flattened list of services, including information such as node, site, and site_uuid.
  const sServicesList: any[] = useAppSelector(
    (state) => state.searchCompute.sServicesList,
  );

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  updates the current list of services from the state array
  //  of service-status updates.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    console.log('SSE: [sUpdateServiceStatus] hook fires [search-compute-table]');

    // loop through the array of service updates, updating the state array.
    for (let i: number = 0; i < sUpdateServiceStatus.length; i++) {
      updateComputeSearchServiceStatus(sUpdateServiceStatus[i]);
    }
    //
  }, [sUpdateServiceStatus]);

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <div className="panel-main" style={{ flexDirection: 'column', padding: '8px' }}>
      <Scrollbox
        id="search-compute-scrollbox"
        direction="vertical"
        width="100%"
        flex="1 1"
        flexDirection="column"
      >
        {sLoadingComponent ? (
          <PleaseWait text="Loading" visible={true} />
        ) : (
          <>
            <Sites servicesList={sServicesList} />
            <div style={{ flex: '1 1 auto' }} />
          </>
        )}
      </Scrollbox>
    </div>
  );
} // SearchComputeTable

//  ------------------------------------------------------------
//
//  Display an icon, description, and a launch button for
//  each item in a list of services.
//
//  ------------------------------------------------------------

function Services(props: { associatedServices: ComputeServiceType[] }) {
  //
  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <div
      id="compute-container"
      style={{
        display: 'block',
        flex: '0 0 auto',
        width: '100%',
        paddingLeft: '15px',
        margin: '20px 0px 20px 0px',
      }}
    >
      {props.associatedServices
        .sort((itemA, itemB) =>
          (itemA.service_name?.toLowerCase() ?? '') >
            (itemB.service_name?.toLowerCase() ?? '') ||
          (itemA.service_name === undefined &&
            itemB.service_name === undefined &&
            (itemA.id ?? 0) > (itemB.id ?? 0))
            ? 1
            : -1,
        )
        .map((hub, hubindex) => (
          <ComputeSession session={hub} />
        ))}
    </div>
  );
  //
} // Services

//  ------------------------------------------------------------
//
//  Display a site, occupying the whole width of the flex box,
//  with a list of services underneath it.
//
//  ------------------------------------------------------------

function SiteTile(props: { site?: Site; row: any }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  C O N T E X T   V A R I A B L E S   A N D   F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  build the site name. we prefix the node is it is not
  //  already prefixed to the site name.
  //
  //  ------------------------------------------------------------

  function siteName(args: { row: any }) {
    //
    var name: string = 'unknown';
    var node: string | undefined = undefined;
    var site: string | undefined = undefined;

    if ('site' in args.row) {
      site = args.row['site'];
    }
    if ('node' in args.row) {
      node = args.row['node'];
    }

    if (site !== undefined) {
      name = site;
    }
    if (site === undefined && node !== undefined) {
      name = node;
    }
    if (
      site !== undefined &&
      node !== undefined &&
      !site.toUpperCase().startsWith(node.toUpperCase())
    ) {
      name = node + ' ' + site;
    }

    return name;
    //
  } // siteName

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <>
      <TabBar
        className="text-colour"
        style={{
          paddingLeft: '10px',
        }}
      >
        <SiteIcon site={props.site} size={30} />
        <div style={{ flex: '0 0 5px' }} />
        <div
          style={{
            flex: '0 0 auto',
            fontSize: '1.2em',
            marginLeft: '10px',
          }}
        >
          {siteName({ row: props.row })}
        </div>
      </TabBar>
      {'services' in props.row ? (
        Services({
          associatedServices: props.row['services'],
        })
      ) : (
        <></>
      )}
    </>
  );
  //
} // SiteTile

//  ------------------------------------------------------------
//
//  Display all the sites, and then the services underneath.
//
//  ------------------------------------------------------------

function Sites(props: { servicesList: Site[] }) {
  //
  //  --------------------------------------------------------------------------
  //
  //  REDUX
  //
  //  --------------------------------------------------------------------------

  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  group services by node and site.
  //
  //  ------------------------------------------------------------

  function groupServices(args: { servicesList: any[] }) {
    //
    let groupedServices: {
      node: string;
      site: string;
      site_uuid: string;
      services: any[];
    }[] = [];

    for (let i: number = 0; i < args.servicesList.length; i++) {
      let index: number = groupedServices.findIndex(
        (item) => item.site_uuid === args.servicesList[i].site.site_uuid,
      );

      if (index === -1) {
        // add a new site
        const newSite: {
          node: string;
          site: string;
          site_uuid: string;
          services: any[];
        } = {
          node: args.servicesList[i].site.node,
          site: args.servicesList[i].site.site,
          site_uuid: args.servicesList[i].site.site_uuid,
          services: [args.servicesList[i]],
        };
        groupedServices.push(newSite);
        //
      } else {
        //
        // add item to an existing site.
        groupedServices[index].services.push(args.servicesList[i]);
        //
      }
    }

    return groupedServices;
    //
  } // groupServices

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <>
      {groupServices({ servicesList: props.servicesList })
        .sort((itemA, itemB) =>
          (itemA.node?.toLowerCase() ?? '') > (itemB.node?.toLowerCase() ?? '') ||
          ((itemA.node?.toLowerCase() ?? '') === (itemB.node?.toLowerCase() ?? '') &&
            (itemA.site?.toLowerCase() ?? '') > (itemB.site?.toLowerCase() ?? ''))
            ? 1
            : -1,
        )
        .map((item, index) => (
          <SiteTile
            key={'site_' + index.toString()}
            site={
              'site_uuid' in item
                ? sD_sites.find((find) => find.site_uuid === item.site_uuid)
                : undefined
            }
            row={item}
          />
        ))}
    </>
  );
  //
} // Sites
